import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Church, Save, Send, CheckCircle2, AlertTriangle, ClipboardEdit, MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";

const FIELDS = [
  { key: "year_founded", label: "Tahun Berdiri", type: "number" },
  { key: "address", label: "Alamat" },
  { key: "distrik", label: "Distrik" },
  { key: "gembala_name", label: "Nama Gembala" },
  { key: "sekretaris_name", label: "Sekretaris" },
  { key: "bendahara_name", label: "Bendahara" },
  { key: "contact_phone", label: "Kontak Gereja" },
  { key: "contact_email", label: "Email Resmi", type: "email" },
  { key: "jumlah_kk", label: "Jumlah KK", type: "number" },
  { key: "jumlah_jiwa", label: "Jumlah Jiwa", type: "number" },
  { key: "laki_laki", label: "Laki-laki", type: "number" },
  { key: "perempuan", label: "Perempuan", type: "number" },
  { key: "dewasa", label: "Dewasa", type: "number" },
  { key: "pemuda", label: "Pemuda/Remaja", type: "number" },
  { key: "anak", label: "Anak / Sekolah Minggu", type: "number" },
  { key: "lansia", label: "Lansia", type: "number" },
  { key: "status_gedung", label: "Status Gedung" },
  { key: "status_tanah", label: "Status Tanah" },
  { key: "kondisi_bangunan", label: "Kondisi Bangunan" },
  { key: "kapasitas", label: "Kapasitas Jemaat", type: "number" },
];

export default function JemaatDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [church, setChurch] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    api.get(`/churches/${id}`)
      .then((r) => { setChurch(r.data); setForm(r.data); })
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat data");
        if (e.response?.status === 403) navigate("/dashboard");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const canEdit = user && (
    user.role === "SUPER_ADMIN" || user.role === "ADMIN_KLASIS" ||
    ((user.role === "SEKRETARIS_JEMAAT" || user.role === "GEMBALA_JEMAAT") && user.church_id === id)
  );
  const canVerify = user && ["SUPER_ADMIN", "ADMIN_KLASIS", "SEKRETARIS_KLASIS"].includes(user.role);
  const canSubmit = user && (
    user.role === "SUPER_ADMIN" ||
    ((user.role === "SEKRETARIS_JEMAAT" || user.role === "GEMBALA_JEMAAT") && user.church_id === id)
  );

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      FIELDS.forEach(({ key, type }) => {
        const v = form[key];
        if (v === "" || v === null || v === undefined) return;
        patch[key] = type === "number" ? Number(v) : v;
      });
      patch.catatan = form.catatan || null;
      const { data } = await api.put(`/churches/${id}/profile`, patch);
      setChurch(data); setForm(data);
      toast.success("Data jemaat berhasil disimpan.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const submitForReview = async () => {
    try {
      await api.post(`/churches/${id}/submit`);
      const { data } = await api.get(`/churches/${id}`);
      setChurch(data);
      toast.success("Data dikirim ke Klasis untuk direview.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const decide = async (decision) => {
    try {
      await api.post(`/churches/${id}/verify`, { decision, notes });
      const { data } = await api.get(`/churches/${id}`);
      setChurch(data); setNotes("");
      toast.success(decision === "VERIFIED" ? "Data terverifikasi." : "Permintaan revisi terkirim.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return <AppShell><div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-96" /></div></AppShell>;
  }
  if (!church) return null;

  return (
    <AppShell>
      <Link to="/jemaat" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-4" data-testid="link-back-jemaat">
        <ArrowLeft className="h-4 w-4" /> Kembali ke daftar
      </Link>

      <Card className="mb-6">
        <CardContent className="p-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Church className="h-7 w-7" />
            </div>
            <div>
              <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">{church.code} • {church.type}</div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl mt-1">{church.name}</h1>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5" /> {church.address || church.distrik || "Timika, Mimika, Papua Tengah"}
              </div>
              {church.verification_notes && church.verification_status === "REVISION_REQUIRED" && (
                <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                  <div><strong>Catatan revisi:</strong> {church.verification_notes}</div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={church.verification_status || "UNVERIFIED"} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex-row items-center justify-between">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <ClipboardEdit className="h-4 w-4" /> Profil Jemaat
          </CardTitle>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button onClick={save} disabled={saving} variant="outline" className="rounded-full" data-testid="btn-save-profile">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan…" : "Simpan"}
              </Button>
            )}
            {canSubmit && church.verification_status !== "SUBMITTED" && church.verification_status !== "VERIFIED" && (
              <Button onClick={submitForReview} className="rounded-full" data-testid="btn-submit-review">
                <Send className="h-4 w-4 mr-2" /> Submit ke Klasis
              </Button>
            )}
            {canVerify && (church.verification_status === "SUBMITTED" || church.verification_status === "UNDER_REVIEW") && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="btn-verify">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Review & Verifikasi
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Review Data Jemaat</DialogTitle>
                    <DialogDescription>Berikan keputusan atas data yang disubmit.</DialogDescription>
                  </DialogHeader>
                  <Textarea
                    placeholder="Catatan review (opsional untuk verifikasi, wajib untuk revisi)"
                    value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="verify-notes"
                  />
                  <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => decide("REVISION_REQUIRED")} data-testid="btn-request-revision">Perlu Revisi</Button>
                    <Button onClick={() => decide("VERIFIED")} data-testid="btn-approve-verified">Verifikasi</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <fieldset disabled={!canEdit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label className="text-xs">{f.label}</Label>
                <Input
                  type={f.type || "text"}
                  value={form[f.key] ?? ""} onChange={(e) => setF(f.key, e.target.value)}
                  placeholder={canEdit ? "" : "—"}
                  data-testid={`input-${f.key}`}
                />
              </div>
            ))}
            <div className="sm:col-span-2 lg:col-span-3 space-y-1.5">
              <Label className="text-xs">Catatan</Label>
              <Textarea value={form.catatan ?? ""} onChange={(e) => setF("catatan", e.target.value)} rows={3} data-testid="input-catatan" />
            </div>
          </fieldset>
          {!canEdit && (
            <div className="mt-6 text-xs text-muted-foreground italic border-t border-border pt-4">
              Anda tidak memiliki hak untuk mengubah data jemaat ini.
            </div>
          )}
          <div className="mt-6 text-xs text-muted-foreground border-t border-border pt-4 grid sm:grid-cols-3 gap-4">
            <div>Terakhir diperbarui: <strong>{church.updated_at ? new Date(church.updated_at).toLocaleString("id-ID") : "—"}</strong></div>
            <div>Oleh: <strong>{church.updated_by || "—"}</strong></div>
            <div>Diverifikasi oleh: <strong>{church.verified_by || "—"}</strong></div>
          </div>
        </CardContent>
      </Card>
    </AppShell>
  );
}
